/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import { useState } from "react";
import { buildAutoRenewLineExtra } from "../../components/membership/buildAutoRenewLineExtra";

// Recurring membership payment: forced (require_*) always renews; optional
// (support_*) renews only when the client ticks the auto-renew checkbox.
// `canOptIn` lets the caller layer page-specific gating on top of the
// invoice-derived support rule (e.g. hide the opt-in once inline payment
// content is shown, or when the invoice already requires recurring). Note
// require_* implies support_*, so pass `!require_*` here to hide the opt-in
// when renewal is mandatory.
export const useRecurringInvoicePayment = (invoice, { canOptIn = true } = {}) => {
  const [autoRenew, setAutoRenew] = useState(false);

  const requireRecurring = !!invoice?.require_recurring_payment_method;
  const supportRecurring = !!invoice?.support_recurring_payment_method;
  const isRecurring = requireRecurring || (supportRecurring && autoRenew);
  const canOptInRecurring = supportRecurring && canOptIn;
  const recurringParams = isRecurring ? { recurring: 1 } : {};

  const renderLineExtra = buildAutoRenewLineExtra({
    invoice,
    autoRenew,
    onAutoRenewChange: (e) => setAutoRenew(e.target.checked),
    enabled: canOptInRecurring,
  });

  return {
    autoRenew,
    setAutoRenew,
    requireRecurring,
    supportRecurring,
    isRecurring,
    canOptInRecurring,
    recurringParams,
    renderLineExtra,
  };
};
